import React from 'react';
import * as Yup from "yup";
import {Grid} from '@material-ui/core';
import {
  calculateCommission,
  calculateMiddleAskingPrice,
  calculateMinimum,
  calculateMoneyTransferCost,
  calculateProfit
} from 'data/utils/calculators';
import TicketDetails from './components/ticketDetails/TicketDetails';
import PriceDetails from './components/priceDetails/PriceDetails';
import {getTodayMidnight} from 'data/utils/shortcuts';
import AbstractProductDetailsForm from '../../../commons/editor/components/abstractProductDetailsForm';

import BaggageDetails from './components/baggageDetails';
import {eCurrency} from 'gui/components/formikCurrencySelector';
import Conditions from '../../../commons/editor/components/abstractProductDetailsForm/components/conditions/Conditions';
import {ITicketDetails} from 'data/interfaces/products';


export interface ITicketDetailsWithTerms extends ITicketDetails {
  acceptedTerms: boolean;
}


interface ITicketDetailsFormProps {
  onSubmit: (formValues) => Promise<any>;
  ticketDetails?: ITicketDetailsWithTerms;
  setSubmitRef?: any;

  commonFormComponents?: any;
}

export const ticketDetailsYupSchemaObject = Yup.object({
//  vacationType: Yup.number().required("יש לבחור סוג חופשה"),
  baggage: Yup.number().min(0).max(100).required("יש לבחור סוג כבודה"),
  ticketsAmount: Yup.number()
    .min(1, "אי אפשר למכור 0 כרטיסים")
    .max(20, "אי אפשר למכור יותר מ 20 כרטיסים")
    .required("נדרש להזין כמות כרטיסים"),
  currentAirlineAllTicketsPrice: Yup.number()
    .min(1, "מחיר כרטיסים צריך להיות גבוהה מ-1")
    .max(999999, "מחיר כרטיסים צריך להיות נמוך מ-999999")
    .required('יש לציין את המחיר של כל הכרטיסים בש"ח נכון להיום'),
  changePrice: Yup.number()
    .min(0, "מחיר שינוי לא יכול להיות שלילי")
    .max(999999, "מחיר שינוי צריך להיות נמוך מ-999999")
    .required("יש להזין את מחיר דמי השינוי לכל הכרטיסים, אם אין עלות שינוי, יש להזין 0")
    .when('currentAirlineAllTicketsPrice', (currentAirlineAllTicketsPrice, schema) => schema.test({
      test: changePrice => {
        if (currentAirlineAllTicketsPrice) {
          const minimum = calculateMinimum(changePrice);
          const middleAskingPrice = calculateMiddleAskingPrice(currentAirlineAllTicketsPrice, minimum);
          const moneyTransferCost = calculateMoneyTransferCost(minimum);
          const commission = calculateCommission(minimum);
          const profit = calculateProfit(middleAskingPrice, changePrice, moneyTransferCost, commission);
          return profit > 0;
        }
        return true;
      },
      message: "מחיר עלות שינוי גבוהה מדיי"
    })),
//  cancelPrice: Yup.number()
//    .min(0, "דמי ביטול לא יכול להיות שלילי")
//    .max(999999, "שמי ביטול צריך להיות נמוך מ-999999")
//    .required("יש לציין דמי ביטול, עם אין יש לרשום 0"),

  currency: Yup.number().min(1, "יש לבחור מטבע").max(10).required("יש לבחור מטבע"),
  originAirport: Yup.object({
      name: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה ממנו הטיסה ממריאה"),
      city: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה ממנו הטיסה ממריאה"),
      country: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה ממנו הטיסה ממריאה"),
      iata: Yup.string().min(1).max(10).required("יש לבחור שדה תעופה ממנו הטיסה ממריאה"),
      x: Yup.number().max(1000),
      y: Yup.number().max(1000),
      elevation: Yup.mixed().optional().nullable(),
      timezone: Yup.mixed().optional().nullable(),
      dst: Yup.mixed().optional().nullable(),
      tz_id: Yup.mixed().optional().nullable(),
      id: Yup.mixed().optional().nullable(),
      apid: Yup.mixed().optional().nullable(),
      icao: Yup.mixed().optional().nullable(),
      name_no_spaces: Yup.string().max(100).optional(),
      priority: Yup.mixed().optional(),
    }
  )
    .required("יש לבחור שדה תעופה מאיפה שהטיסה יוצאת")
    .noUnknown(true),
  destinationAirport: Yup.object({
      name: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה בו הטיסה נוחתת"),
      city: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה בו הטיסה נוחתת"),
      country: Yup.string().min(1).max(100).required("יש לבחור שדה תעופה בו הטיסה נוחתת"),
      iata: Yup.string().min(1).max(10).required("יש לבחור שדה תעופה בו הטיסה נוחתת"),
      x: Yup.number().max(1000),
      y: Yup.number().max(1000),
      elevation: Yup.mixed().optional().nullable(),
      timezone: Yup.mixed().optional().nullable(),
      dst: Yup.mixed().optional().nullable(),
      tz_id: Yup.mixed().optional().nullable(),
      id: Yup.mixed().optional().nullable(),
      apid: Yup.mixed().optional().nullable(),
      icao: Yup.mixed().optional().nullable(),
      name_no_spaces: Yup.string().max(100).optional(),
      priority: Yup.mixed().optional(),
    }
  )
    .required("יש לבחור שדה תעופה הסופי איפה שהטיסה נוחתת")
    .when('originAirport', (originAirport, schema) => schema.test({
      test: destinationAirport => (destinationAirport && originAirport) ? (destinationAirport !== originAirport && destinationAirport.iata !== originAirport.iata) : true,
      message: "לא ניתן לבחור שדה תעופה שממנו יוצאים"
    }))
    .noUnknown(true),
  originFlightCompany: Yup.object({
    name: Yup.string().min(1).max(100).required("יש לבחור חברת תעופה הלוך"),
    iata: Yup.string().min(1).max(10).required("יש לבחור חברת תעופה הלוך"),
    icao: Yup.string().min(1).max(10).required("יש לבחור חברת תעופה הלוך"),
    country: Yup.string().min(1).max(100).required("יש לבחור חברת תעופה הלוך").optional().nullable(),
    id: Yup.mixed().optional().nullable(),
    alid: Yup.mixed().optional().nullable(),
    callsign: Yup.mixed().optional().nullable(),
    name_no_spaces: Yup.string().max(100).optional(),
    priority: Yup.mixed().optional(),
  }).required("יש לבחור חברת תעופה הלוך")
    .noUnknown(true),
  destinationFlightCompany: Yup.mixed()
    .when("isRoundTripTicket", {
      is: true,
      then: Yup.object({
        name: Yup.string().min(1).max(100).required("יש לבחור חברת תעופה הלוך"),
        iata: Yup.string().min(1).max(10).required("יש לבחור חברת תעופה הלוך"),
        icao: Yup.string().min(1).max(10).required("יש לבחור חברת תעופה הלוך"),
        country: Yup.string().min(1).max(100).required("יש לבחור חברת תעופה הלוך").optional().nullable(),
        id: Yup.mixed().optional().nullable(),
        alid: Yup.mixed().optional().nullable(),
        callsign: Yup.mixed().optional().nullable(),
        name_no_spaces: Yup.string().max(100).optional(),
        priority: Yup.mixed().optional(),
      })
        .required("יש לבחור חברת תעופה חזור")
        .noUnknown(true)
    }),
  originTimeOfTakeOff: Yup.number().min(getTodayMidnight().getTime()).required("יש לבחור זמן המראה").nullable(),
  originTimeOfLanding: Yup.number().required("יש לבחור זמן נחיתה").nullable()
    .when('originTimeOfTakeOff', (originTimeOfTakeOff, schema) => schema.test({
      test: originTimeOfLanding => originTimeOfLanding > originTimeOfTakeOff,
      message: "זמן נחיתה לא יכול להיות לפני זמן המראה"
    }))
    .when('originTimeOfTakeOff', (originTimeOfTakeOff, schema) => schema.test({
      test: originTimeOfLanding => (originTimeOfLanding - originTimeOfTakeOff) <= 24 * 1000 * 60 * 60,
      message: "טיסה לא יכולה להימשך יותר מ-24 שעות"
    })),
  destinationTimeOfTakeOff: Yup.mixed()
    .when("isRoundTripTicket", {
      is: true,
      then: Yup.number().required("יש לבחור זמן המראה").when('originTimeOfLanding', (originTimeOfLanding, schema) => schema.test({
        test: destinationTimeOfTakeOff => originTimeOfLanding < destinationTimeOfTakeOff,
        message: "זמן המראה חזור לא יכול להיות לפני זמן נחיתה הלוך"
      }))
    })
    .nullable(),
  destinationTimeOfLanding: Yup.mixed()
    .when("isRoundTripTicket", {
      is: true,
      then: Yup.number()
        .required("יש לבחור זמן נחיתה")
        .when('destinationTimeOfTakeOff', (destinationTimeOfTakeOff, schema) => schema.test({
          test: destinationTimeOfLanding => destinationTimeOfLanding > destinationTimeOfTakeOff,
          message: "זמן נחיתה לא יכול להיות לפני זמן המראה"
        }))
        .when('destinationTimeOfTakeOff', (destinationTimeOfTakeOff, schema) => schema.test({
          test: destinationTimeOfLanding => (destinationTimeOfLanding - destinationTimeOfTakeOff) <= 24 * 1000 * 1 * 60 * 60,
          message: "טיסה לא יכולה להימשך יותר מ-24 שעות"
        }))
    })
    .nullable(),

  handBag: Yup.boolean(),
  isRoundTripTicket: Yup.boolean(),
  additionalServices: Yup.string().max(10000),
  acceptedTerms: Yup.boolean()
    .required("Required")
    .oneOf([true], "יש לאשר את התנאים לפני השימוש"),
}).noUnknown(true);

export const ticketDetailsInitialValues = {
//  vacationType: "",
  baggage: "",
  ticketsAmount: 1,
  currency: eCurrency.SHEKEL,
  originAirport: undefined,
  destinationAirport: undefined,
  originTimeOfTakeOff: null,
  originTimeOfLanding: null,
  destinationTimeOfTakeOff: null,
  destinationTimeOfLanding: null,
  isRoundTripTicket: false,
  handBag: false,

  additionalServices: "",
  destinationFlightCompany: undefined,
  originFlightCompany: undefined,
  currentAirlineAllTicketsPrice: undefined,
  acceptedTerms: false,
  changePrice: undefined,
  // cancelPrice: undefined,
};

export const TicketDetailsInputs = ({
                                      submitForm,
                                      submitCount,
                                      isSubmitting,
                                      setFieldValue,
                                      values,
                                      touched,
                                      errors,
                                      forcedRoundTrip
                                    }) => <>
  <TicketDetails forcedRoundTrip={forcedRoundTrip} values={values} errors={errors} touched={touched}
                 setFieldValue={setFieldValue}/>
  <Grid item xs={12} style={{marginTop: 0}}/>
  <BaggageDetails values={values}/>
</>;

const TicketDetailsForm = (props: ITicketDetailsFormProps) => (<AbstractProductDetailsForm
  {...props}
  initialValues={{
    ...ticketDetailsInitialValues,
    ...props.ticketDetails
  }}
  yupSchemaObject={ticketDetailsYupSchemaObject}
  formComponents={({submitForm, submitCount, isSubmitting, setFieldValue, values, touched, errors}) => (
    <>
      <TicketDetailsInputs
        {...{submitForm, submitCount, isSubmitting, setFieldValue, values, touched, errors, forcedRoundTrip: false}}
      />
      <Grid item xs={12} style={{marginTop: 30}}/>
      <PriceDetails values={values}/>
      <Grid item xs={12} style={{marginTop: 30}}/>
      <Conditions values={values}/>
    </>
  )}
  onSubmit={(formValues) => props
    .onSubmit({
      ticketDetails: formValues
    })}
/>);

TicketDetailsForm.defaultProps = {
  onSubmit: (formValues) => new Promise((resolve) => {
    
    setTimeout(resolve, 1000);
  }),
  ticketDetails: {},
  setSubmitRef: () => {
  },
  commonFormComponents: () => null,
};

export default React.memo(TicketDetailsForm);
